import React from "react";
import { PlayerFlag } from "../../components/Flag";

export default function SoccerField({
  slots,
  roster,
  previewSlot,
  previewPlayer,
  compatibleSlots = [],
  onSlotClick,
  viewedPlayer,
  onLockedClick,
  dragging,
  dragCompatibleSlots = [],
  onDragStart,
  onDragEnd,
  onDrop,
}) {
  return (
    <div className="h-full w-full grid place-items-center min-h-0">
      <div
        className="relative h-full aspect-[2/3] max-w-full rounded-2xl
               bg-gradient-to-b from-emerald-950/60 via-emerald-950/30 to-emerald-950/50
               border border-zinc-800 overflow-hidden"
      >
        <FieldMarkings />
        {slots.map((slot) => {
          const locked = roster[slot.id];
          const isPreview = !locked && previewSlot === slot.id;
          const isEligible =
            !locked && !isPreview && compatibleSlots.includes(slot.id);
          const isViewed =
            !!locked && !!viewedPlayer && locked.id === viewedPlayer.id;
          const isBeingDragged = dragging?.sourceSlotId === slot.id;
          const isDropTarget =
            !!dragging &&
            !isBeingDragged &&
            dragCompatibleSlots.includes(slot.id);

          return (
            <Slot
              key={slot.id}
              slot={slot}
              player={locked || (isPreview ? previewPlayer : null)}
              locked={!!locked}
              preview={isPreview}
              eligible={isEligible}
              viewed={isViewed}
              isBeingDragged={isBeingDragged}
              isDropTarget={isDropTarget}
              onSlotClick={onSlotClick}
              onLockedClick={onLockedClick}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDrop={onDrop}
            />
          );
        })}
      </div>
    </div>
  );
}

function FieldMarkings() {
  return (
    <svg
      viewBox="0 0 100 150"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full text-emerald-400/15"
    >
      <rect
        x="2"
        y="2"
        width="96"
        height="146"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.4"
        rx="1"
      />
      <line
        x1="2"
        y1="75"
        x2="98"
        y2="75"
        stroke="currentColor"
        strokeWidth="0.4"
      />
      <circle
        cx="50"
        cy="75"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.4"
      />
      <circle cx="50" cy="75" r="0.6" fill="currentColor" />
      <rect
        x="22"
        y="2"
        width="56"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.4"
      />
      <rect
        x="37"
        y="2"
        width="26"
        height="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.4"
      />
      <rect
        x="22"
        y="130"
        width="56"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.4"
      />
      <rect
        x="37"
        y="141"
        width="26"
        height="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.4"
      />
    </svg>
  );
}

function SlotFace({ player }) {
  return <PlayerFlag nation={player.nation} />;
}

function Slot({
  slot,
  player,
  locked,
  preview,
  eligible,
  viewed,
  isBeingDragged,
  isDropTarget,
  onSlotClick,
  onLockedClick,
  onDragStart,
  onDragEnd,
  onDrop,
}) {
  const clickable = eligible || locked;
  // A slot is draggable when there's a player in it (either locked or actively previewed).
  const draggable = !!player && (locked || preview);
  const Wrapper = clickable ? "button" : "div";

  const wrapperProps = {
    style: { top: `${slot.top}%`, left: `${slot.left}%` },
    ...(clickable && {
      type: "button",
      onClick: () => {
        if (locked) onLockedClick?.(slot.id);
        else if (eligible) onSlotClick?.(slot.id);
      },
    }),
    ...(draggable && {
      draggable: true,
      onDragStart: () => onDragStart?.(slot.id, !!locked),
      onDragEnd: () => onDragEnd?.(),
    }),
    onDragOver: (e) => {
      if (isDropTarget) e.preventDefault();
    },
    onDrop: (e) => {
      if (!isDropTarget) return;
      e.preventDefault();
      onDrop?.(slot.id);
    },
  };

  return (
    <Wrapper
      {...wrapperProps}
      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center group"
    >
      <div
        className={`w-14 h-14 rounded-full grid place-items-center overflow-hidden transition-all duration-200
          ${
            viewed
              ? "bg-zinc-900 border-2 border-emerald-400 shadow-xl shadow-emerald-400/60 ring-2 ring-emerald-400/40 scale-110"
              : locked
                ? "bg-zinc-900 border-2 border-emerald-500/80 shadow-md shadow-emerald-500/20 group-hover:border-emerald-400 group-hover:shadow-lg group-hover:shadow-emerald-400/40"
                : preview
                  ? "bg-zinc-900/90 border-2 border-emerald-400 shadow-lg shadow-emerald-500/50 animate-pulse"
                  : eligible
                    ? "bg-zinc-900/40 border-2 border-zinc-100/60 cursor-pointer " +
                      "shadow-lg shadow-zinc-500/50 animate-pulse " +
                      "group-hover:border-emerald-400 group-hover:bg-zinc-900/90"
                    : "bg-zinc-950/50 border border-dashed border-zinc-700"
          }
          ${isBeingDragged ? "opacity-40" : ""}
          ${isDropTarget ? "ring-2 ring-emerald-400/60 ring-offset-2 ring-offset-emerald-950/60 shadow-lg shadow-emerald-400/40" : ""}`}
      >
        {player ? (
          <SlotFace player={player} />
        ) : (
          <div
            className={`text-sm font-semibold tracking-wider transition-colors
              ${
                eligible
                  ? "text-zinc-100 group-hover:text-emerald-300"
                  : "text-zinc-500"
              }`}
          >
            {slot.label}
          </div>
        )}
      </div>

      {player && (
        <div className="mt-1 max-w-[120px]">
          <span
            className={`text-sm font-medium truncate block text-center
              ${preview ? "text-emerald-300" : "text-zinc-300"}`}
          >
            {surname(player.name)}
          </span>
        </div>
      )}
    </Wrapper>
  );
}

function surname(name) {
  const parts = name.split(" ");
  return parts[parts.length - 1];
}
